import { useAuth0 } from '@auth0/auth0-react';
import { useCopyToClipboard } from '@base/hooks/use-copy-to-clipboard';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@basic';
import { UIPaths } from '@consts';
import { useShareReferralCode } from '@hooks/useShareReferralCode';
import { EditIcon, HeartEmptyIcon, HeartFullIcon, MoreHorizontalIcon, ShareIcon } from '@icons';
import { GoalModel, ToastType, VersionedEntityType } from '@models';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import GoalBottomSheet from './GoalBottomSheet';

export default function GoalActions({
  goal,
  showActions = {
    versionType: true,
    like: true,
    share: true,
    more: true,
    edit: false
  }
}: {
  goal: GoalModel;
  showActions?: {
    versionType?: boolean;
    like?: boolean;
    share?: boolean;
    more?: boolean;
    edit?: boolean;
  };
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [showBottomSheet, setShowBottomSheet] = useState({
    show: false,
    position: { x: 0, y: 0 }
  });
  const [liked, setLiked] = useState(null);
  const { copy } = useCopyToClipboard();
  const shareReferralCode = useShareReferralCode();
  const { like, unlike, setToast } = useStore((state) => ({
    like: state.like,
    unlike: state.unlike,
    setToast: state.setToast
  }));

  const { isAuthenticated } = useAuth0();

  const onUnlike = (event) => {
    event.preventDefault();
    event.stopPropagation();

    unlike('GOAL', goal.entityId)
      .then((success) => {
        setLiked(false);
      })
      .catch((error) => {
        setToast({ message: t('toasts.somethingWentWrong'), type: ToastType.ERROR });
      });
  };

  const onLike = (event) => {
    event.preventDefault();
    event.stopPropagation();

    like('GOAL', goal.entityId)
      .then((success) => {
        setLiked(true);
      })
      .catch((error) => {
        setToast({ message: t('toasts.somethingWentWrong'), type: ToastType.ERROR });
      });
  };

  useEffect(() => {
    setLiked(goal.liked?.isActive);
  }, [goal.liked]);

  const onShowMoreAction = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onShowMore(event);
  };

  const onShowMore = (e) => {
    setShowBottomSheet({
      show: true,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const onBottomSheetClose = () => {
    setShowBottomSheet({
      show: false,
      position: { x: 0, y: 0 }
    });
  };

  let priorityColor = undefined;
  if (goal.subscription) {
    switch (goal.subscription.priority) {
      case 'LOW':
        priorityColor = 'text-label-yellow';
        break;
      case 'MEDIUM':
        priorityColor = 'text-label-orange';
        break;
      case 'HIGH':
        priorityColor = 'text-label-red';
        break;
    }
  }

  const getVersionTypeLabel = (versionType: VersionedEntityType) => {
    if (showActions.versionType && goal.versionType && goal.user?.isMe) {
      switch (versionType) {
        case VersionedEntityType.DRAFT:
          return <Label text={t('components.goalActions.draft.label')} type='secondary' />;
        case VersionedEntityType.PUBLISHED:
          return <Label text={t('components.goalActions.published.label')} type='secondary' />;
      }
    }
  };

  const onGoToEdit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    router.push(UIPaths.goalEditor(goal.entityId));
  };

  return (
    <div className='flex flex-row gap-md items-center justify-between w-full'>
      {/* available version types */}
      <div className='flex-shrink flex flex-row items-left'>
        {goal.availableVersionTypes
          ?.filter(
            (versionType) =>
              versionType === VersionedEntityType.DRAFT ||
              versionType === VersionedEntityType.PUBLISHED
          )
          .map((versionType) => (
            <div
              className='pl-sm flex-shrink flex flex-row items-center'
              key={`${goal.entityId}-${versionType}`}
            >
              {getVersionTypeLabel(versionType)}
            </div>
          ))}
      </div>
      <div className='flex-shrink flex flex-row gap-md items-center'>
        {/* edit */}
        {showActions.edit && (
          <button
            onClick={onGoToEdit}
            className='cursor-pointer bg-transparent border-none p-0 flex items-center justify-center'
            type='button'
            aria-label={t('components.goalActions.edit.label', 'Edit goal')}
          >
            <EditIcon size={24} className={classNames(setupForDark('text-icon-primary'))} />
          </button>
        )}
        {/* like */}
        {showActions.like && isAuthenticated && (
          <div className='cursor-pointer bg-transparent border-none p-0 flex items-center justify-center'>
            {liked ? (
              <HeartFullIcon
                size={20}
                className={classNames(setupForDark('text-icon-primary'))}
                onClick={onUnlike}
                aria-label={t('components.goalActions.unlike.label', 'Unlike goal')}
              />
            ) : (
              <HeartEmptyIcon
                size={20}
                className={classNames(setupForDark('text-icon-primary'))}
                onClick={onLike}
                aria-label={t('components.goalActions.like.label', 'Like goal')}
              />
            )}
          </div>
        )}

        {/* share */}
        {showActions.share && (
          <ShareIcon
            size={24}
            className={classNames(setupForDark('text-icon-primary'), 'cursor-pointer')}
            onClick={(e) => copy(UIPaths.shareableGoal(goal.entityId, shareReferralCode), e)}
            aria-label={t('components.goalActions.share.label', 'Share goal')}
            data-testid={`goal-actions-share-button-${goal.entityId}`}
          />
        )}

        {/* more */}
        {showActions.more && (
          <div className='relative'>
            <MoreHorizontalIcon
              size={28}
              className={classNames(setupForDark('text-icon-quaternary'), 'cursor-pointer')}
              onClick={onShowMoreAction}
              aria-label={t('components.goalActions.more.label', 'More actions')}
            />
            {showBottomSheet.show && (
              <GoalBottomSheet
                isOpen={showBottomSheet.show}
                onClose={onBottomSheetClose}
                goal={goal}
                position={showBottomSheet.position}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
